import { useMemo, useState } from 'react';
import type { Currency, Owner } from '../lib/types';
import type { ProjectedPlacement } from '../lib/forecast';
import {
  splitForSelection,
  countCreditForSelection,
  selectionHasOwner,
  selectionForOids,
  type Selection,
} from '../lib/splits';
import { formatMoney, formatDate } from '../lib/format';

export interface BreakdownPerson {
  owner: Owner;
  /** Perm-desk member whose shares route in by deal currency. */
  routed: boolean;
}

interface Props {
  projected: ProjectedPlacement[];
  people: BreakdownPerson[];
  /** Currencies that route perm-desk shares into the current view. */
  routedCurrencies: ReadonlySet<string>;
  currency: Currency;
  viewDate: string;
  onSelectPerson: (oid: string) => void;
}

type SortKey = 'name' | 'book' | 'runners' | 'credit';

const EMPTY_SET: ReadonlySet<string> = new Set();

export function TeamBreakdown({
  projected,
  people,
  routedCurrencies,
  currency,
  viewDate,
  onSelectPerson,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('book');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    const live = projected.filter(
      (pp) => pp.placement.startDate <= viewDate && pp.effectiveEnd >= viewDate,
    );

    const computed = people.map(({ owner, routed }) => {
      const sel: Selection = routed
        ? { direct: EMPTY_SET, routed: new Set([owner.oid]), routedCurrencies }
        : selectionForOids(new Set([owner.oid]));
      let book = 0;
      let runners = 0;
      let credit = 0;
      for (const pp of live) {
        if (!selectionHasOwner(pp.placement, sel)) continue;
        runners += 1;
        book += splitForSelection(pp.placement, sel);
        credit += countCreditForSelection(pp.placement, sel);
      }
      return { owner, routed, book, runners, credit };
    });

    // Only people with something on the date in view — no zero rows.
    // Routed perm-desk contributors render as a separate "Other" section.
    const visible = computed.filter((r) => r.book > 0 || r.runners > 0);

    const compare = (a: (typeof computed)[number], b: (typeof computed)[number]) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'name': return dir * a.owner.name.localeCompare(b.owner.name);
        case 'runners': return dir * (a.runners - b.runners);
        case 'credit': return dir * (a.credit - b.credit);
        case 'book': return dir * (a.book - b.book);
      }
    };

    return {
      team: visible.filter((r) => !r.routed).sort(compare),
      other: visible.filter((r) => r.routed).sort(compare),
    };
  }, [projected, people, routedCurrencies, viewDate, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const allRows = [...rows.team, ...rows.other];
  const totals = allRows.reduce(
    (acc, r) => ({ book: acc.book + r.book, credit: acc.credit + r.credit }),
    { book: 0, credit: 0 },
  );

  if (allRows.length === 0) return null;

  return (
    <section>
      <div className="ds-sec">
        <h2>
          Team <em>per person on {formatDate(viewDate)}.</em>
        </h2>
        <span>click a person to drill into their book</span>
      </div>

      <div className="ds-tw">
        <table className="ds-table">
          <thead>
            <tr>
              <Th label="Person" active={sortKey === 'name'} dir={sortDir} onClick={() => toggleSort('name')} />
              <Th label="WGP" active={sortKey === 'book'} dir={sortDir} onClick={() => toggleSort('book')} align="right" />
              <Th label="Runners" active={sortKey === 'runners'} dir={sortDir} onClick={() => toggleSort('runners')} align="right" />
              <Th label="Runners by split" active={sortKey === 'credit'} dir={sortDir} onClick={() => toggleSort('credit')} align="right" />
            </tr>
          </thead>
          <tbody>
            {rows.team.map((r) => (
              <BreakdownRow key={r.owner.oid} row={r} currency={currency} onSelectPerson={onSelectPerson} />
            ))}
            {rows.other.length > 0 && (
              <tr>
                <td colSpan={4} style={{ fontSize: 12, color: 'var(--text-3)', paddingTop: 14 }}>
                  Other · shares routed in by deal currency
                </td>
              </tr>
            )}
            {rows.other.map((r) => (
              <BreakdownRow key={r.owner.oid} row={r} currency={currency} onSelectPerson={onSelectPerson} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ background: 'var(--panel-head)', fontWeight: 600, borderBottom: 'none' }}>Total</td>
              <td className="num" style={{ background: 'var(--panel-head)', fontWeight: 600, textAlign: 'right', borderBottom: 'none' }}>
                {formatMoney(totals.book, currency, true)}
              </td>
              <td style={{ background: 'var(--panel-head)', borderBottom: 'none' }} />
              <td className="num" style={{ background: 'var(--panel-head)', fontWeight: 600, textAlign: 'right', borderBottom: 'none' }}>
                {totals.credit.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
        WGP and Runners by split partition exactly — each person&rsquo;s share of a deal counts once, so
        the totals match the headline figures. Runners overlap where a deal has several owners, so that
        column has no total.
      </p>
    </section>
  );
}

function BreakdownRow({
  row: r,
  currency,
  onSelectPerson,
}: {
  row: { owner: Owner; book: number; runners: number; credit: number };
  currency: Currency;
  onSelectPerson: (oid: string) => void;
}) {
  return (
    <tr onClick={() => onSelectPerson(r.owner.oid)} style={{ cursor: 'pointer' }}>
      <td className="ds-name">{r.owner.name}</td>
      <td className="num" style={{ textAlign: 'right' }}>{formatMoney(r.book, currency, true)}</td>
      <td className={'num' + (r.runners === 0 ? ' dim' : '')} style={{ textAlign: 'right' }}>{r.runners}</td>
      <td className={'num' + (r.credit === 0 ? ' dim' : '')} style={{ textAlign: 'right' }}>
        {r.credit.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
      </td>
    </tr>
  );
}

function Th({
  label,
  active,
  dir,
  onClick,
  align,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'right';
}) {
  return (
    <th className={active ? 'sorted' : ''} style={align ? { textAlign: align } : undefined}>
      <button onClick={onClick}>
        {label}
        {active && <span style={{ color: 'var(--perm)', marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}
